<template>
  <div
    class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
    v-if="isVisible"
  >
    <div class="bg-white rounded-lg text-left p-6 w-full max-w-md">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-lg font-semibold text-gray-900">
          Booking System Layout
        </h2>
        <BaseButton
          type="button"
          @click="closeModal"
          class="text-black hover:text-gray-600 transition-colors p-1"
        >
          X
        </BaseButton>
      </div>

      <!-- Content -->
      <div class="space-y-4">
        <!-- Layout Name Input -->
        <div>
          <BaseLabel
            forAttr="layoutName"
            text="Layout Name"
            class="block text-sm font-medium text-gray-700 mb-2"
          />
          <BaseInput
            id="layoutName"
            v-model="layoutName"
            type="text"
            placeholder="Enter layout name"
            className="w-full p-2 border border-gray-300 rounded-md"
          />
        </div>

        <!-- First Layout Option -->
        <div
          class="p-2 border rounded-md cursor-pointer transition-all bg-white relative"
          :class="
            selectedLayout === 'single'
              ? 'border-purple-600'
              : 'border-gray-300'
          "
          @click="selectLayout('single')"
        >
          <div
            class="flex justify-between items-center p-4 rounded transition-colors"
            :class="
              selectedLayout === 'single' ? 'bg-purple-50' : 'bg-gray-100'
            "
          >
            <span class="text-sm font-medium text-gray-500">Single Layout</span>
            <img
              src="@/assets/expert-program/directory/single-layout.svg"
              alt="Basic Layout"
              class="w-26 h-26"
            />
          </div>
          <div
            v-if="selectedLayout === 'single'"
            class="absolute bottom-2 right-2 text-primary-purple"
          >
            <div v-html="CHECK_CIRCLE" class="w-5 h-5"></div>
          </div>
        </div>

        <!-- Second Layout Option -->
        <div
          class="p-2 border rounded-md cursor-pointer transition-all bg-white relative"
          :class="
            selectedLayout === 'grid' ? 'border-purple-600' : 'border-gray-300'
          "
          @click="selectLayout('grid')"
        >
          <div
            class="flex justify-between items-center p-4 rounded transition-colors"
            :class="selectedLayout === 'grid' ? 'bg-purple-50' : 'bg-gray-100'"
          >
            <span class="text-sm font-medium text-gray-500">Grid Layout</span>
            <img
              src="../../../../assets/expert-program/directory/grid-layout.svg"
              alt="Advanced Layout"
              class="w-26 h-26"
            />
          </div>
          <div
            v-if="selectedLayout === 'grid'"
            class="absolute bottom-2 right-2 text-primary-purple"
          >
            <div v-html="CHECK_CIRCLE" class="w-5 h-5"></div>
          </div>
        </div>

        <!-- Third Layout Option -->
        <div
          class="p-2 border rounded-md cursor-pointer transition-all bg-white relative"
          :class="
            selectedLayout === 'row' ? 'border-purple-600' : 'border-gray-300'
          "
          @click="selectLayout('row')"
        >
          <div
            class="flex justify-between items-center p-4 rounded transition-colors"
            :class="selectedLayout === 'row' ? 'bg-purple-50' : 'bg-gray-100'"
          >
            <span class="text-sm font-medium text-gray-500">Row Layout</span>
            <img
              src="../../../../assets/expert-program/directory/row-layout.svg"
              alt="Custom Layout"
              class="w-26 h-26"
            />
          </div>
          <div
            v-if="selectedLayout === 'row'"
            class="absolute bottom-2 right-2 text-primary-purple"
          >
            <div v-html="CHECK_CIRCLE" class="w-5 h-5"></div>
          </div>
        </div>
      </div>

      <!-- Alert Messages -->
      <div v-if="showAlert" class="mt-4">
        <div
          class="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3"
        >
          <div v-if="!layoutName" class="mb-1">
            • Please enter a layout name
          </div>
          <div v-if="!selectedLayout">• Please select a layout type</div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end mt-6">
        <BaseButton
          type="button"
          @click="createLayout"
          class="bg-primary-purple text-white px-6 py-2 rounded-md font-medium hover:bg-primary-purple/90"
        >
          Create Layout
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue";
import BaseButton from "@/components/shared/BaseButton.vue";
import BaseInput from "@/components/shared/BaseInput.vue";
import BaseLabel from "@/components/shared/BaseLabel.vue";
import { CHECK_CIRCLE } from "@/assets/svg/shared/svg-constants";
import { CreateLayoutData } from "@/types/directory-types";

export default defineComponent({
  name: "CreateNewDirectoryLayoutModal",
  components: {
    BaseButton,
    BaseInput,
    BaseLabel,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
  },
  emits: {
    close: () => true,
    createLayout: (data: CreateLayoutData) => true,
  },
  setup(props, { emit }) {
    const layoutName = ref("");
    const selectedLayout = ref("");

    watch(
      () => props.isVisible,
      (newValue) => {
        if (newValue) {
          layoutName.value = "";
          selectedLayout.value = "";
        }
      }
    );

    const closeModal = () => {
      emit("close");
    };

    const selectLayout = (layoutType: string) => {
      selectedLayout.value = layoutType;
    };

    const showAlert = ref(false);

    const createLayout = () => {
      if (layoutName.value && selectedLayout.value) {
        const layoutData: CreateLayoutData = {
          layoutName: layoutName.value,
          layoutType: selectedLayout.value,
        };
        emit("createLayout", layoutData);
      } else {
        showAlert.value = true;
        setTimeout(() => {
          showAlert.value = false;
        }, 1500);
      }
    };

    return {
      layoutName,
      selectedLayout,
      showAlert,
      closeModal,
      createLayout,
      selectLayout,
      CHECK_CIRCLE,
    };
  },
});
</script>
